import { useState, useEffect, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG — swap image / copy here without touching component logic
// ─────────────────────────────────────────────────────────────────────────────
const POPUP_CONFIG = {
  // Encode the filename spaces so the browser resolves it correctly
  imageSrc: "/assets/Nuuma_Design_BANER_Eshre.webp",
  imageAlt: "Nuuma Genetics na 42. Kongresie ESHRE w Londynie – stoisko C36",
  heading: "Będziemy obecni podczas 42. Kongresu ESHRE w Londynie",
  body: "Zapraszamy pacjentów i specjalistów medycyny rozrodu do rozmowy o możliwościach zaawansowanej diagnostyki genetycznej wspierającej skuteczniejsze leczenie niepłodności, ograniczenie ryzyka poronień oraz bardziej świadomy dobór zarodków w procedurach IVF.",
  subBody:
    "Dowiedz się więcej o naszych rozwiązaniach z zakresu genetyki reprodukcyjnej i diagnostyki PGT-A.",
  badge: "Stoisko C36",
};

const STORAGE_KEY = "nuuma_eshre_popup_count";
const MAX_SHOWS = 4;
const DELAY_MS = 10_000; // 10 seconds

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function EventPopup() {
  // ── TEMPORARILY DISABLED ── remove this line to re-enable the popup
  return null;
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const shouldShow = useCallback(() => {
    try {
      return parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10) < MAX_SHOWS;
    } catch { return true; }
  }, []);

  const incrementCount = useCallback(() => {
    try {
      const n = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      localStorage.setItem(STORAGE_KEY, String(n + 1));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!shouldShow()) return;
    const t = setTimeout(() => { setVisible(true); incrementCount(); }, DELAY_MS);
    return () => clearTimeout(t);
  }, [shouldShow, incrementCount]);

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => { setVisible(false); setLeaving(false); }, 380);
  }, []);

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes nuuma-popup-in {
          from { transform: translateY(100%); }
          to   { transform: translateY(0);    }
        }
        @keyframes nuuma-popup-out {
          from { transform: translateY(0);    opacity: 1; }
          to   { transform: translateY(100%); opacity: 0; }
        }
        .nuuma-event-popup {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 9999;
          animation: nuuma-popup-in 0.5s cubic-bezier(0.34, 1.2, 0.64, 1) forwards;
          /* full-width, hugs the very bottom — no gap */
          box-shadow: 0 -8px 48px rgba(239, 96, 121, 0.35);
        }
        .nuuma-event-popup.leaving {
          animation: nuuma-popup-out 0.38s ease-in forwards;
        }
      `}</style>

      <div
        className={`nuuma-event-popup${leaving ? " leaving" : ""}`}
        role="dialog"
        aria-modal="false"
        aria-label="Ogłoszenie: ESHRE 2026 Londyn"
      >
        {/* Outer wrapper — coral semi-transparent, rounded-t-[40px] like footer */}
        <div
          className="rounded-t-[40px] text-white relative"
          style={{
            backgroundColor: "rgb(239 96 121 / 91%)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >

          {/* ── Close button ── */}
          <button
            onClick={dismiss}
            aria-label="Zamknij ogłoszenie"
            className="
              absolute top-4 right-5 z-10
              w-9 h-9 flex items-center justify-center
              rounded-full
              bg-white/10 hover:bg-white/25
              text-white text-sm leading-none
              transition-colors duration-200
              cursor-pointer border-0
            "
          >
            ✕
          </button>

          {/* ── Inner layout (mirrors footer's px / pt / pb) ── */}
          <div className="nuuma-container">
            <div className="
              flex flex-col sm:flex-row
              items-center sm:items-stretch
              gap-5 sm:gap-8 lg:gap-12
              px-2 sm:px-4 lg:px-8
              pt-8 sm:pt-6 lg:pt-8
              pb-6 sm:pb-5 lg:pb-7
            ">

              {/* ── LEFT: Banner image ── */}
              <div className="
                flex-shrink-0
                w-full sm:w-auto
                flex items-center justify-center
                sm:justify-start
              ">
                <img
                  src={POPUP_CONFIG.imageSrc}
                  alt={POPUP_CONFIG.imageAlt}
                  className="
                    block
                    w-full max-w-[340px]
                    sm:h-28 sm:w-auto sm:max-w-none
                    lg:h-56
                    object-contain
                  "
                />
              </div>

              {/* ── RIGHT: Text content ── */}
              <div className="flex-1 min-w-0 flex flex-col justify-center sm:border-l sm:border-white/10 sm:pl-8 lg:pl-12">

                {/* Booth badge — navy for contrast on coral background */}
                <span className="
                  inline-flex items-center gap-1.5 self-start
                  bg-nuuma-primary text-white
                  text-[10px] sm:text-[11px] font-bold tracking-widest uppercase
                  px-3 py-1 rounded-full
                  mb-2.5
                ">
                  📍 {POPUP_CONFIG.badge}
                </span>

                {/* Heading */}
                <h2 className="
                  mt-0 mb-2
                  text-sm sm:text-[15px] lg:text-base
                  font-bold leading-snug text-white
                  pr-8 sm:pr-0
                ">
                  {POPUP_CONFIG.heading}
                </h2>

                {/* Body */}
                <p className="
                  text-[11.5px] sm:text-xs lg:text-[13px]
                  text-white/80 leading-relaxed
                  mb-1.5
                  hidden sm:block
                ">
                  {POPUP_CONFIG.body}
                </p>

                {/* Sub-body */}
                <p className="
                  text-[11px] sm:text-[11.5px]
                  text-white/55 italic leading-relaxed
                  hidden sm:block
                ">
                  {POPUP_CONFIG.subBody}
                </p>

                {/* Mobile-only shortened body */}
                <p className="
                  text-[11.5px] text-white/75 leading-relaxed
                  sm:hidden
                  mb-0
                ">
                  {POPUP_CONFIG.subBody}
                </p>
              </div>
            </div>
          </div>

          {/* ── Bottom accent bar — navy strip ── */}
          <div className="h-[3px] bg-nuuma-primary/60" />
        </div>
      </div>
    </>
  );
}
